/**
 * useDealPresence(dealId) — join the loan's `deal:{id}` viewer group while a loan
 * detail page is open, so the backend can target "current viewers" of that loan
 * (collaborative-awareness notifications) and answer "who's viewing this".
 *
 * Membership is server-authorized: the backend checks `can_view_loan` before
 * adding the connection to the group (clients can't self-join a loan they can't
 * see). Enter on mount, exit on unmount/loan-change. No-ops cleanly when realtime
 * isn't configured (503) — the enter/exit calls just fail quietly.
 *
 * Call once on the loan drilldown screen: `useDealPresence(loanId)`.
 */
import { useEffect } from "react";

import { realtimeApi } from "../api/realtimeApi";
import { useRealtime } from "./RealtimeProvider";

export function useDealPresence(dealId) {
  const { connectionId } = useRealtime();

  useEffect(() => {
    // Need both a loan and a live connection to be a "viewer".
    if (!dealId || !connectionId) return undefined;

    let active = true;
    realtimeApi.presenceEnter(dealId, connectionId).catch(() => {
      // 503 not-configured / 403 not-permitted → just don't register presence.
    });

    return () => {
      active = false;
      realtimeApi.presenceExit(dealId, connectionId).catch(() => {
        // best effort on unmount
      });
      void active;
    };
  }, [dealId, connectionId]);
}

export default useDealPresence;
